import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import JourneysCard from './JourneysCard';
import MessagingControls from './MessagingControls';
import StepModal from './StepModal';
import SummaryCards from './SummaryCards';
import { stepChannel } from './overviewModel';
import { cardStyle } from './ui';
import useJourneys from '../JourneysTab/useJourneys';
import SectionState from '../shared/SectionState';

import SectionErrorBoundary from '@/routes/Agency/WorkerV2/components/SectionErrorBoundary';
import { JourneyStep, LifecycleJourney } from '@/services/lifecycleAPI';
import { trackLifecycleFeatureUsed } from '@/util/vexo';

// The Lifecycle journeys page from the design. The full engine settings,
// call task queue, message editor and system events stay one click away in
// the header links (Lifecycle/index.tsx).
const Overview = () => {
  const { data, loading, error, reload, refresh, updateJourney } =
    useJourneys('overview');
  const [, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<{
    journey: LifecycleJourney;
    step: JourneyStep;
  } | null>(null);

  if (!data) {
    return (
      <div style={cardStyle}>
        <SectionState
          error={error ?? undefined}
          mode={loading ? 'loading' : 'error'}
          title="engine"
          onRetry={reload}
        />
      </div>
    );
  }

  // A journey name opens that journey's settings, sends and exits in the
  // engine settings view (the detail panel the old table opened in place).
  const openJourney = (key: string) =>
    setSearchParams(new URLSearchParams({ tab: 'settings', open: key }));

  return (
    <div
      data-testid="lifecycleOverview"
      style={{ display: 'flex', flexDirection: 'column', gap: 32 }}
    >
      <SectionErrorBoundary label="the summary cards">
        <SummaryCards data={data} />
      </SectionErrorBoundary>
      <SectionErrorBoundary label="messaging controls">
        <MessagingControls
          data={data}
          onRefresh={refresh}
          onSave={(changes) => updateJourney('_global', changes)}
        />
      </SectionErrorBoundary>
      <SectionErrorBoundary label="the journeys table">
        <JourneysCard
          journeys={data.journeys}
          onOpenJourney={openJourney}
          onOpenStep={(journey, step) => {
            trackLifecycleFeatureUsed({
              feature: 'step_open',
              surface: 'overview',
              journey_key: journey.journey_key,
              step_key: step.key,
              channel: stepChannel(step),
            });
            setModal({ journey, step });
          }}
        />
      </SectionErrorBoundary>
      {modal && (
        <StepModal
          journey={modal.journey}
          key={modal.step.key}
          step={modal.step}
          onClose={(changed) => {
            setModal(null);
            if (changed) reload();
          }}
        />
      )}
    </div>
  );
};

export default Overview;
